import type { DeviceApi, HeartbeatPayload } from './api/device-api.js';
import type { TimeSource } from './clock.js';
import type { Logger } from './log.js';

/**
 * Heartbeat emitter (technical-design §6.6): every §6.6 field, plus the clock offset
 * from design-addendum §11. This is the source of the fleet dashboard's "screens dark
 * right now" (dwf-035), so it is emitted whether or not the last one succeeded.
 */

export interface HeartbeatEmitter {
  build(): Promise<HeartbeatPayload>;
  beat(): Promise<'accepted' | 'deferred' | 'rejected' | 'revoked'>;
}

export interface HeartbeatEmitterOptions {
  readonly api: DeviceApi;
  readonly time: TimeSource;
  readonly logger: Logger;
  readonly appVersion: string;
  readonly bootedAtDeviceMs: number;
  readonly cacheBytes: () => Promise<number>;
  /** Reported by the native shell over the JS bridge; null in a browser tab. */
  readonly freeBytes: () => number | null;
  readonly manifestVersion: () => number | null;
  readonly lastError: () => string | null;
  readonly queuedEvents: () => Promise<number>;
  /** Reported by the native shell over the JS bridge (D174); null in a browser tab. */
  readonly wifiSsid: () => string | null;
  /**
   * dwf-063: whether the player held the foreground at send time, over the JS bridge.
   *
   * `null` means "cannot tell" — a browser tab has no shell to ask — and the fleet
   * dashboard must read it that way, never as backgrounded. Without it a device sitting
   * behind Amazon's launcher heartbeats normally and shows green, which is the state
   * D211's waiver now makes routine.
   */
  readonly foreground: () => boolean | null;
  /**
   * dwf-064 census, all over the JS bridge and all `null` without a shell. `Build.MODEL`,
   * `Build.VERSION.SDK_INT`, the OS build string and whether the `SYSTEM_ALERT_WINDOW`
   * app-op is actually held — the last is the difference between a screen the watchdog can
   * recover and one it cannot (D179), and today it is known only to the device.
   */
  readonly hardwareModel: () => string | null;
  readonly osVersion: () => string | null;
  readonly sdkInt: () => number | null;
  readonly overlayOpGranted: () => boolean | null;
  /**
   * dwf-064: `wifi | ethernet | unknown`. The heartbeat carries `wifi_ssid` and nothing
   * else about the link, so an Ethernet-connected screen reports no SSID and silently
   * exempts itself from D174's relocation check. This turns "no SSID" from a dead end into
   * a fact.
   */
  readonly networkType: () => string | null;
  /**
   * dwf-064 / D213: whether the television panel itself is on, over the JS bridge. A panel
   * staff switched into standby may keep heartbeating, and without this it reads like the
   * launcher blip D211 accepts. `null` without a shell, and never read as off.
   */
  readonly screenOn: () => boolean | null;
  /** dwf-064 §2: the shell's signing-certificate SHA-256 — debug key or release key. */
  readonly signingSha256: () => string | null;
}

export function createHeartbeatEmitter(options: HeartbeatEmitterOptions): HeartbeatEmitter {
  const build = async (): Promise<HeartbeatPayload> => ({
    app_version: options.appVersion,
    cache_bytes: await options.cacheBytes(),
    free_bytes: options.freeBytes(),
    manifest_ver: options.manifestVersion(),
    uptime_s: Math.max(
      0,
      Math.floor((options.time.deviceNowMs() - options.bootedAtDeviceMs) / 1000),
    ),
    last_error: options.lastError(),
    wifi_ssid: options.wifiSsid(),
    foreground: options.foreground(),
    hardware_model: options.hardwareModel(),
    os_version: options.osVersion(),
    sdk_int: options.sdkInt(),
    overlay_op_granted: options.overlayOpGranted(),
    network_type: options.networkType(),
    screen_on: options.screenOn(),
    signing_sha256: options.signingSha256(),
    // The device's clock, not the corrected one: the server's whole point is to measure how
    // wrong it is, so sending the already-corrected value would report an offset of zero from a
    // stick whose clock is a year out.
    device_clock_at: new Date(options.time.deviceNowMs()).toISOString(),
    clock_offset_ms: options.time.offsetMs(),
    clock_drift_flagged: options.time.driftExceedsThreshold(),
    queued_events: await options.queuedEvents(),
  });

  return {
    build,
    async beat() {
      const payload = await build();
      const result = await options.api.postHeartbeat(payload);
      if (result.status === 'accepted') return 'accepted';
      if (result.status === 'revoked') return 'revoked';
      if (result.status === 'unavailable') {
        options.logger.info('heartbeat.deferred', { reason: result.reason });
        return 'deferred';
      }
      options.logger.warn('heartbeat.rejected', { http_status: result.http_status });
      return 'rejected';
    },
  };
}
